import { BOOKER_EMAIL_QUESTION_ID } from '@/constants/constants';
import { ROUTE_DASHBOARD_BOOKING_PAGE, ROUTE_DASHBOARD_BOOKING_PAGES } from '@/constants/routes';
import { END_UPDATE_MESSAGE, START_UPDATE_MESSAGE } from '@/hooks/useBookingPagePreview';
import {
	useCreateEventTypeMutation,
	useEditEventTypeMutation,
	useGetEventTypeQuery,
	useRemoveEventTypeMutation,
} from '@/queries/eventTypes.queries';
import eventTypeService from '@/services/eventType.service';
import uuidService from '@/services/uuid.service';
import { IEventTypeWrite } from '@/types/apiClient/IEventTypeWrite';
import IEventType from '@/types/IEventType';
import IQuestion from '@/types/IQuestion';
import generateSlug from '@/utils/generateSlug';
import { useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';

export default function useEventTypeSuccessForm() {
	const toast = useToast();
	const history = useHistory();
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const getEventTypeQuery = useGetEventTypeQuery({ id });
	const createEventTypeMutation = useCreateEventTypeMutation();
	const editEventTypeMutation = useEditEventTypeMutation();
	const eventType = getEventTypeQuery.data;
	const queryRemove = useRemoveEventTypeMutation();
	const [isMigrating, setIsMigrating] = useState(false);

	useEffect(
		/**
		 * En los event-type antiguos el email era un campo aparte.
		 * A partir de julio de 2023 pasa a ser una pregunta más con un identificador conocido.
		 * Para hacer este cambio retrocompatible es necesario migrar los event-type antiguos.
		 */
		function _migrateQuestions() {
			// Query still loading. Do not do nothing yet.
			if (!getEventTypeQuery.data) {
				return;
			}
			// Migrating action was triggered. Don't do anything.
			if (isMigrating) {
				return;
			}

			// If there is already a question with the email id no action required
			if (getEventTypeQuery.data.questions.some((q) => q.id === BOOKER_EMAIL_QUESTION_ID)) {
				return;
			}

			setIsMigrating(true);

			// Otherwise add an email question and update the event type
			const questions: Array<IQuestion> = [
				{
					id: BOOKER_EMAIL_QUESTION_ID,
					label: t('Email'),
					required: true,
					type: 'EMAIL',
				},
				...getEventTypeQuery.data.questions,
			];

			return editEventTypeMutation.mutate({ ...getEventTypeQuery.data, questions });
		},
		[isMigrating, editEventTypeMutation, getEventTypeQuery, t]
	);

	function copyLink(eventType: IEventType) {
		navigator.clipboard.writeText(eventTypeService.getLink(eventType.fullSlug));
		toast({
			status: 'success',
			title: t('Link copied.'),
		});
	}

	function deleteEventType() {
		if (!window.confirm(t('Do you want to delete this booking page?'))) {
			return;
		}

		return queryRemove.mutate(
			{ id },
			{
				onSuccess() {
					history.push(ROUTE_DASHBOARD_BOOKING_PAGES);
					toast({
						status: 'success',
						title: t('Deleted.'),
					});
				},
			}
		);
	}

	function duplicateEventType(eventType: IEventType) {
		const id = uuidService.v4();
		createEventTypeMutation
			.mutateAsync({
				...eventType,
				id,
				name: t('Copy of') + ' ' + eventType.name,
				slug: generateSlug(),
			})
			.then(() => {
				history.push(ROUTE_DASHBOARD_BOOKING_PAGE.replace(':id', id));
				toast({ status: 'success', title: t('Event type duplicated.') });
			})
			.catch(() => {
				toast({ status: 'error', title: t('An error ocurred, try again later.') });
			});
	}

	function handleChange(update: Partial<IEventTypeWrite>) {
		if (!eventType) {
			return;
		}
		const iframe = document.getElementById('booking-page-preview') as HTMLIFrameElement;
		if (iframe) {
			iframe.contentWindow?.postMessage({ type: START_UPDATE_MESSAGE });
		}
		return editEventTypeMutation.mutate(
			{ ...eventType, ...update },
			{
				onError(error) {
					Sentry.captureException(new Error(`ERROR_SAVING_EVENT_TYPE`));

					if (error.response?.status === 409) {
						return toast({
							title: t('Error'),
							description: t('There is already a booking page with this link. Please change the link and try again.'),
							status: 'error',
						});
					}

					return toast({
						title: t('Error'),
						description: t('We cannot save the changes. Please reload the page and try again.'),
						status: 'error',
					});
				},
				onSettled() {
					if (iframe) {
						iframe.contentWindow?.postMessage({ type: END_UPDATE_MESSAGE });
					}
				},
			}
		);
	}

	return {
		copyLink: eventType ? () => copyLink(eventType) : () => {},
		deleteEventType: eventType ? () => deleteEventType() : () => {},
		duplicateEventType: eventType ? () => duplicateEventType(eventType) : () => {},
		handleChange,
		t,
		getEventTypeQuery,
		queryEdit: editEventTypeMutation,
		eventType,
	};
}
